/* ============================================================
   SCP Cart drawer behaviour. Mirrors components/cart/CartDrawer.tsx.
   - window.scpCart = { open, close, refresh }
   - open()  : fetch /cart.js, re-render line items + totals
   - /cart/change.js for qty +/- and remove (by line key)
   - /cart/add.js for cross-sell "Add" buttons
   - header/mobile cart badges ([data-scp-cart-count]) sync on every change
   - delivery timeline dates computed client-side (business-day aware)
   Money is rendered from shop.money_format (window.scpMoneyFormat).
   ============================================================ */
(function () {
  var root = document.querySelector('[data-scp-cart]');
  if (!root) return;

  var body = root.querySelector('[data-scp-cart-body]');
  var foot = root.querySelector('[data-scp-cart-foot]');
  var sidePanel = root.querySelector('[data-scp-cart-sidepanel]');
  var sidePanelList = sidePanel && sidePanel.querySelector('[data-scp-cart-sidepanel-list]');
  var moneyFormat = window.scpMoneyFormat || 'R{{amount}}';
  var emptyHtml = window.scpCartEmptyHtml || '<div class="scp-cart__empty"><p class="scp-cart__empty-title">Your cart is empty</p></div>';

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, parts.join('.'));
  }

  // Lightweight, dependency-free toast so cart failures are never silent.
  var toastEl = null, toastTimer = null;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'scp-cart-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-shown');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-shown'); }, 3500);
  }

  var knownCount = null;
  function setCount(count) {
    knownCount = count;
    document.querySelectorAll('[data-scp-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count < 1;
    });
    var label = root.querySelector('[data-scp-cart-count-label]');
    if (label) label.textContent = '(' + count + ' ' + (count === 1 ? 'item' : 'items') + ')';
  }
  // Optimistic badge update for instant feedback before the network round-trip.
  function bumpCount(delta) {
    var base = knownCount;
    if (base === null) { var b = document.querySelector('[data-scp-cart-count]'); base = b ? (parseInt(b.textContent, 10) || 0) : 0; }
    setCount(Math.max(0, base + delta));
  }

  // Cart endpoints can intermittently return HTTP 429 ("Verifying your
  // connection" bot-challenge / throttle), especially on preview/proxy
  // environments and flaky mobile networks. Retry a couple of times with
  // backoff so a transient throttle self-heals instead of dead-ending the
  // add/remove. Resolves with the Response; callers still check r.ok.
  function cartFetch(url, opts, attempt) {
    attempt = attempt || 0;
    return fetch(url, opts).then(function (r) {
      if (r.status === 429 && attempt < 2) {
        var wait = 600 * (attempt + 1);
        return new Promise(function (res) { setTimeout(res, wait); })
          .then(function () { return cartFetch(url, opts, attempt + 1); });
      }
      return r;
    });
  }

  // Sequence guard: only the latest refresh() may apply, so rapid/concurrent
  // refreshes can't resolve out of order and leave a stale count on the badge.
  var refreshSeq = 0;
  function refresh() {
    var seq = ++refreshSeq;
    return cartFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart load failed (' + r.status + ')'); return r.json(); })
      .then(function (cart) { if (seq === refreshSeq) render(cart); return cart; })
      .catch(function () {});
  }

  function revealSidePanel() {
    if (sidePanel && sidePanelList && sidePanelList.children.length > 0 && !root.hidden) {
      sidePanel.classList.add('is-shown');
    }
  }
  function hideSidePanel() { if (sidePanel) sidePanel.classList.remove('is-shown'); }

  function open() {
    root.hidden = false;
    document.body.style.overflow = 'hidden';
    refresh();
    setTimeout(revealSidePanel, 350);
  }
  function close() {
    root.hidden = true;
    document.body.style.overflow = '';
    hideSidePanel();
  }

  // Centralized add — the single entry point every add path (PDP, product card,
  // cross-sell, mobile + desktop) calls so the badge and drawer stay in sync with
  // no page reload. Optimistically bumps the badge, then reconciles via refresh().
  function add(variantId, quantity, opts) {
    opts = opts || {};
    var qty = quantity || 1;
    bumpCount(qty);
    return cartFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) {
          // 429/HTML challenge has no JSON body — surface a usable error.
          return r.json().then(function (e) { throw e; }, function () { throw new Error('add failed (' + r.status + ')'); });
        }
        return r.json();
      })
      .then(function (line) {
        refresh();
        document.dispatchEvent(new CustomEvent('scp:cart:added', { detail: line }));
        if (opts.open) open();
        return line;
      })
      .catch(function (err) {
        bumpCount(-qty); // revert optimistic bump (e.g. sold out / error)
        refresh();
        document.dispatchEvent(new CustomEvent('scp:cart:error', { detail: err }));
        throw err;
      });
  }

  window.scpCart = { open: open, close: close, refresh: refresh, add: add, setCount: setCount, bumpCount: bumpCount };

  // Any component can signal a cart change and the drawer (single source of truth)
  // re-syncs the badge + contents. Guarded refresh() keeps it race-safe.
  document.addEventListener('scp:cart:changed', function () { refresh(); });

  // Surface add failures (sold out, throttled endpoint, network) instead of
  // letting the optimistic badge silently snap back with no explanation.
  document.addEventListener('scp:cart:error', function (e) {
    var err = e && e.detail;
    var msg = (err && (err.description || err.message)) || '';
    if (/sold ?out|not available|unavailable|inventory|out of stock/i.test(msg)) {
      toast('Sorry, that item is currently unavailable.');
    } else {
      toast("Couldn't add to cart. Please try again.");
    }
  });

  // ---- Delivery timeline dates (Ordered today, Shipped +1 biz day, Delivered +4 more)
  function fmtDate(d) { return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
  function addBizDays(from, days) {
    var r = new Date(from); var added = 0;
    while (added < days) {
      r.setDate(r.getDate() + 1);
      var dow = r.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return r;
  }
  function fillTimeline() {
    var ordered = root.querySelector('[data-scp-date="ordered"]');
    var shipped = root.querySelector('[data-scp-date="shipped"]');
    var delivered = root.querySelector('[data-scp-date="delivered"]');
    if (!ordered) return;
    var now = new Date();
    var ship = addBizDays(now, 1);
    var deliver = addBizDays(ship, 4);
    ordered.textContent = fmtDate(now);
    if (shipped) shipped.textContent = 'by ' + fmtDate(ship);
    if (delivered) delivered.textContent = 'by ' + fmtDate(deliver);
  }

  // Strike-through price for a line: prefer a line-level (bundle/automatic)
  // discount, else the variant compare-at sale price. Mirrors the Liquid render.
  function lineStruck(item) {
    if (item.original_line_price > item.final_line_price) return item.original_line_price;
    if (item.compare_at_price && item.compare_at_price > item.price) return item.compare_at_price * item.quantity;
    return 0;
  }

  function itemHtml(item) {
    var imgSrc = item.image ? item.image.replace(/(\.[^.\/?]+)(\?.*)?$/, '_144x$1$2') : '';
    var img = imgSrc ? '<img src="' + imgSrc + '" alt="" width="72" height="72" loading="lazy">' : '';
    var variant = (item.variant_title && item.variant_title.indexOf('Default') === -1)
      ? '<p class="scp-cart__variant">' + item.variant_title + '</p>' : '';
    var struck = lineStruck(item);
    var compare = struck > 0 ? '<span class="scp-cart__compare">' + formatMoney(struck) + '</span>' : '';
    var unitHint = item.quantity > 1
      ? '<p class="scp-cart__unit-hint">' + formatMoney(item.final_price) + ' × ' + item.quantity + '</p>' : '';
    return '<li class="scp-cart__item" data-scp-line data-key="' + item.key + '">' +
        '<a href="' + item.url + '" class="scp-cart__thumb">' + img + '</a>' +
        '<div class="scp-cart__info"><div>' +
          '<a href="' + item.url + '" class="scp-cart__name">' + item.product_title + '</a>' + variant +
        '</div><div class="scp-cart__info-bottom">' +
          '<div class="scp-cart__pricecol"><div class="scp-cart__priceline">' +
            '<span class="scp-cart__price" data-scp-line-total>' + formatMoney(item.final_line_price) + '</span>' + compare +
          '</div>' + unitHint + '</div>' +
          '<div class="scp-cart__qty">' +
            '<button type="button" data-scp-qty-dec aria-label="Decrease quantity"' + (item.quantity <= 1 ? ' disabled' : '') + '>−</button>' +
            '<span data-scp-qty>' + item.quantity + '</span>' +
            '<button type="button" data-scp-qty-inc aria-label="Increase quantity"' + (item.quantity >= 10 ? ' disabled' : '') + '>+</button>' +
          '</div>' +
        '</div></div>' +
        '<button type="button" class="scp-cart__remove" data-scp-line-remove aria-label="Remove item">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>' +
        '</button>' +
      '</li>';
  }

  // ---- Re-render line items + totals from a /cart.js payload. We replace ONLY
  //      the items list so the server-rendered "You May Also Like" strip and the
  //      delivery timeline survive a JS mutation (they used to be wiped). When
  //      the body had no list yet (cart was empty at page load), we rebuild the
  //      stack and lazily fetch a cross-sell strip via the recommendations API.
  function render(cart) {
    setCount(cart.item_count);
    if (cart.item_count === 0) {
      body.innerHTML = emptyHtml;
      foot.hidden = true;
      return;
    }
    foot.hidden = false;

    var itemsHtml = cart.items.map(itemHtml).join('');
    var list = body.querySelector('[data-scp-cart-items]');
    if (list) {
      list.innerHTML = itemsHtml;
    } else {
      body.innerHTML = '<div class="scp-cart__stack"><ul class="scp-cart__items" data-scp-cart-items>' + itemsHtml + '</ul></div>';
    }

    // Footer: subtotal + bundle/automatic discount summary
    var sub = foot.querySelector('[data-scp-cart-subtotal]');
    if (sub) sub.textContent = formatMoney(cart.total_price);
    var savings = foot.querySelector('[data-scp-cart-savings]');
    if (savings) {
      if (cart.total_discount > 0) {
        savings.hidden = false;
        var orig = foot.querySelector('[data-scp-cart-original]');
        var disc = foot.querySelector('[data-scp-cart-discount]');
        if (orig) orig.textContent = formatMoney(cart.original_total_price);
        if (disc) disc.textContent = '−' + formatMoney(cart.total_discount);
      } else {
        savings.hidden = true;
      }
    }

    ensureCrossSell(cart);
  }

  // ---- "You May Also Like" — keep the server-rendered strip/panel when present;
  //      otherwise build them from Shopify's related-products recommendations so
  //      the upsell appears even when the cart was empty at page load. Mobile uses
  //      the inline strip; desktop uses the slide-in side panel.
  function csImg(p, w) {
    var src = p.featured_image ? p.featured_image.replace(/(\.[^.\/?]+)(\?.*)?$/, '_' + w + 'x$1$2') : (p.images && p.images[0] || '');
    return src ? '<img src="' + src + '" alt="" width="72" height="72" loading="lazy">' : '';
  }
  function inlineCard(p) {
    var v = (p.variants || [])[0] || {};
    return '<div class="scp-cart__cs-card">' +
        '<a href="' + p.url + '" class="scp-cart__cs-media">' + csImg(p, 240) + '</a>' +
        '<div class="scp-cart__cs-body">' +
          '<p class="scp-cart__cs-title">' + p.title + '</p>' +
          '<p class="scp-cart__cs-price">' + formatMoney(p.price) + '</p>' +
          '<button type="button" class="scp-btn scp-btn--outline scp-btn--size-sm scp-cart__cs-add" data-scp-cs-add data-variant-id="' + v.id + '">Add</button>' +
        '</div></div>';
  }
  function sideCard(p) {
    var v = (p.variants || [])[0] || {};
    var compare = (p.compare_at_price && p.compare_at_price > p.price)
      ? '<span class="scp-cart__sp-compare">' + formatMoney(p.compare_at_price) + '</span>' : '';
    return '<div class="scp-cart__sp-card">' +
        '<a href="' + p.url + '" class="scp-cart__sp-media">' + csImg(p, 144) + '</a>' +
        '<div class="scp-cart__sp-info">' +
          '<a href="' + p.url + '" class="scp-cart__sp-titlelink"><h4 class="scp-cart__sp-title">' + p.title + '</h4></a>' +
          '<div class="scp-cart__sp-pricerow"><span class="scp-cart__sp-price">' + formatMoney(p.price) + '</span>' + compare + '</div>' +
          '<button type="button" class="scp-btn scp-btn--outline scp-btn--size-sm scp-cart__sp-add" data-scp-cs-add data-variant-id="' + v.id + '">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg><span>Add</span>' +
          '</button>' +
        '</div></div>';
  }
  function ensureCrossSell(cart) {
    var stack = body.querySelector('.scp-cart__stack');
    var needInline = stack && !stack.querySelector('[data-scp-cart-crosssell]');
    var needSide = sidePanelList && sidePanelList.children.length === 0;
    if (!needInline && !needSide) return;
    var firstId = cart.items[0] && cart.items[0].product_id;
    if (!firstId) return;
    var inCart = cart.items.map(function (i) { return i.product_id; });
    fetch('/recommendations/products.json?product_id=' + firstId + '&limit=10&intent=related', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var recs = (data.products || []).filter(function (p) { return inCart.indexOf(p.id) === -1 && p.available; }).slice(0, 6);
        if (!recs.length) return;
        if (needInline) {
          var wrap = document.createElement('div');
          wrap.className = 'scp-cart__crosssell';
          wrap.setAttribute('data-scp-cart-crosssell', '');
          wrap.innerHTML = '<h3 class="scp-cart__sub">You May Also Like</h3><div class="scp-cart__cs-scroll scp-scrollbar-hide">' + recs.map(inlineCard).join('') + '</div>';
          var list = stack.querySelector('[data-scp-cart-items]');
          if (list && list.parentNode === stack) stack.insertBefore(wrap, list.nextSibling);
          else stack.appendChild(wrap);
        }
        if (needSide) {
          sidePanelList.innerHTML = recs.map(sideCard).join('');
          revealSidePanel();
        }
      })
      .catch(function () {});
  }

  function changeLine(key, quantity, lineEl) {
    if (lineEl) lineEl.setAttribute('data-updating', '');
    // change.js returns the full updated cart, so we render() directly from it.
    // No scp:cart:changed dispatch here — that would trigger a second, redundant
    // /cart.js refresh and double the request rate (which itself invites 429s).
    cartFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      // A throttled/challenged endpoint (HTTP 429 "Verifying your connection")
      // returns an HTML page, not JSON — guard so we don't silently no-op.
      .then(function (r) { if (!r.ok) throw new Error('cart change failed (' + r.status + ')'); return r.json(); })
      .then(function (cart) { render(cart); })
      .catch(function (err) {
        if (lineEl) lineEl.removeAttribute('data-updating');
        toast("Couldn't update your cart. Please try again.");
      });
  }

  function addVariant(variantId, btn) {
    var label = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; }
    // Use the centralized add (optimistic badge + guarded refresh).
    add(variantId, 1)
      .then(function () { if (btn) { btn.disabled = false; } })
      .catch(function () { if (btn) { btn.disabled = false; btn.innerHTML = label; } });
  }

  // ===== Delegated clicks =====
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-scp-cart-open]')) { e.preventDefault(); open(); return; }
    var closeEl = e.target.closest('[data-scp-cart-close]');
    if (closeEl) {
      // If the close target is a real link (e.g. empty-state collection rows,
      // Shop All / Shop Hair, Continue Shopping <a>), let it navigate — just
      // close the drawer. Only block default for non-link controls (X, backdrop).
      var navLink = closeEl.tagName === 'A' ? closeEl : closeEl.closest('a[href]');
      if (navLink && navLink.getAttribute('href') && navLink.getAttribute('href') !== '#') { close(); return; }
      e.preventDefault(); close(); return;
    }

    var csAdd = e.target.closest('[data-scp-cs-add]');
    if (csAdd && root.contains(csAdd)) { e.preventDefault(); addVariant(csAdd.getAttribute('data-variant-id'), csAdd); return; }

    var line = e.target.closest('[data-scp-line]');
    if (!line || !root.contains(line)) return;
    var key = line.getAttribute('data-key');
    var qtyEl = line.querySelector('[data-scp-qty]');
    var qty = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
    if (e.target.closest('[data-scp-qty-inc]')) { changeLine(key, qty + 1, line); }
    else if (e.target.closest('[data-scp-qty-dec]')) { changeLine(key, Math.max(0, qty - 1), line); }
    else if (e.target.closest('[data-scp-line-remove]')) { changeLine(key, 0, line); }
  });

  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !root.hidden) close(); });

  // ===== Mobile: swipe the drag handle down to close =====
  (function () {
    var handle = root.querySelector('[data-scp-cart-handle]');
    var panel = root.querySelector('.scp-cart__panel');
    if (!handle || !panel) return;
    var startY = 0, dy = 0, dragging = false;
    handle.addEventListener('touchstart', function (e) {
      startY = e.touches[0].clientY; dy = 0; dragging = true;
      panel.style.transition = 'none';
    }, { passive: true });
    handle.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      dy = e.touches[0].clientY - startY;
      if (dy > 0) { panel.style.transform = 'translateY(' + dy + 'px)'; e.preventDefault(); }
    }, { passive: false });
    function end() {
      if (!dragging) return;
      dragging = false;
      panel.style.transition = '';
      panel.style.transform = '';
      if (dy > 100) close();
    }
    handle.addEventListener('touchend', end);
    handle.addEventListener('touchcancel', end);
  })();

  // Draw the timeline dates on load. Header/mobile badges are already correct
  // from the server render; setCount keeps them in sync after each change.
  fillTimeline();
})();
